import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { injectStripe, CardElement, IbanElement } from 'react-stripe-elements';
import TextField from 'app/commonComponents/TextField';
import RadioInput from 'app/commonComponents/RadioInput';
import InlineAlert from 'app/commonComponents/InlineAlert';
import facility from 'app/facility';

import PayPalButtons from './_paypalButton';
import CreditCardIcons from './_creditCardIcons';
import {
  Container,
  RadioButtons,
  PaymentType,
  PaymentBlock,
  PaymentOptionsHeading,
} from './styles';

const createOptions = (padding) => {
  return {
    hidePostalCode: true,
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        letterSpacing: '0.025em',
        fontFamily: 'Source Code Pro, monospace',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding,
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };
};

const {
  settings: {
    currency,
    paypalClientId,
    stripePublishKey,
    stripeSepaEnabled,
    supportsBambora,
  },
} = facility;

@observer
class PaymentOptions extends Component {
  constructor(props) {
    super(props);
    props.form.setStripeObject(props.stripe);
  }

  ibanElementRef = createRef();

  componentDidMount() {
    if (window.Stripe && this.ibanElementRef.current) {
      // temporary solution until we have moved from react-stripe-elements to @stripe/react-stripe-js
      this.stripeSEPA = window.Stripe(stripePublishKey);
      const ibanElement = this.stripeSEPA.elements().create('iban', {
        supportedCountries: ['SEPA'],
        style: createOptions().style,
      });
      ibanElement.mount(this.ibanElementRef.current);
      this.props.form.setIbanElement(ibanElement);
      this.props.form.setStripeSEPAObject(this.stripeSEPA);
    }
  }

  render() {
    const { price, form, payPalDirectBuy } = this.props;
    const {
      paymentTypes,
      formValues,
      errors,
      setFormValue,
      processPaymentFailure,
      validatePayment,
      validateField,
    } = form;
    const hasStripeProvider = !!stripePublishKey;
    const hasPayPalProvider = !!paypalClientId;
    const hasBamboraProvider = supportsBambora;
    // todo let's consider which value we can use for invoice without using pk
    const reference = i18n.sprintf(
      i18n.gettext('Ticket purchase %s.'),
      'invoiceNumber'
    );

    const shouldBeUnfocusable =
      [
        hasStripeProvider,
        stripeSepaEnabled && hasStripeProvider,
        hasBamboraProvider && !hasStripeProvider,
        hasPayPalProvider,
      ].filter(Boolean).length === 1;

    const ibanForm = window.Stripe ? (
      <div>
        <TextField
          placeHolder={i18n.gettext('Account holder name')}
          required
          onChange={(e) => setFormValue('owner_name', e.target.value)}
          onBlur={() => validateField('owner_name')}
          value={formValues.get('owner_name')}
          error={
            !!errors.get('owner_name') && i18n.gettext('Please enter a name')
          }
        />
        <TextField
          placeHolder={i18n.gettext('Account holder email')}
          required
          onChange={(e) => setFormValue('owner_email', e.target.value)}
          onBlur={() => validateField('owner_email')}
          value={formValues.get('owner_email')}
          error={
            !!errors.get('owner_email') &&
            i18n.gettext('Please enter a valid email')
          }
        />
        <div ref={this.ibanElementRef}></div>
      </div>
    ) : (
      <InlineAlert type="error">
        {i18n.gettext(
          'Error loading Stripe - pleas choose another payment method.'
        )}
      </InlineAlert>
    );

    const cardForm = (
      <div>
        <TextField
          required
          placeHolder={i18n.gettext('Account Owner')}
          onChange={(e) => setFormValue('owner_name', e.target.value)}
          onBlur={() => validateField('owner_name')}
          value={formValues.get('owner_name')}
          error={
            !!errors.get('owner_name') && i18n.gettext('Please enter a name')
          }
        />
        <CardElement {...createOptions()} />
      </div>
    );

    const payPalForm = (
      <PayPalButtons
        purchaseUnits={[
          {
            amount: {
              value: price,
              currency_code: currency,
            },
            description: reference,
          },
        ]}
        onProcessSuccess={(orderID) => {
          setFormValue('token', orderID);
          if (payPalDirectBuy) validatePayment();
        }}
        onProcessError={processPaymentFailure}
        style={{
          size: 'responsive',
        }}
        isAuthorize
      />
    );

    const ibanElement = <div>{ibanForm}</div>;

    const creditElement = hasStripeProvider && <div>{cardForm}</div>;

    const payPalElement = <div>{payPalForm}</div>;

    const paymentType = formValues.get('payment_type');
    return (
      <Container className="card-form">
        <PaymentOptionsHeading>
          {i18n.gettext('Payment method')}
        </PaymentOptionsHeading>
        <RadioButtons>
          {hasStripeProvider && (
            <PaymentType
              onClick={() => setFormValue('payment_type', paymentTypes.credit)}
            >
              <RadioInput
                name="payment-method"
                tabIndex={shouldBeUnfocusable ? -1 : 0}
                checked={paymentType === paymentTypes.credit}
                onChange={() =>
                  setFormValue('payment_type', paymentTypes.credit)
                }
                label={i18n.gettext('Credit card')}
                labelStyle={{ fontWeight: 'initial' }}
              />
            </PaymentType>
          )}
          {hasStripeProvider && stripeSepaEnabled && (
            <PaymentType
              onClick={() => setFormValue('payment_type', paymentTypes.sepa)}
            >
              <RadioInput
                name="payment-method"
                tabIndex={shouldBeUnfocusable ? -1 : 0}
                checked={paymentType === paymentTypes.sepa}
                onChange={() => setFormValue('payment_type', paymentTypes.sepa)}
                label={i18n.gettext('Direct debit')}
                labelStyle={{ fontWeight: 'initial' }}
              />
            </PaymentType>
          )}
          {!hasStripeProvider && hasBamboraProvider && (
            <PaymentType
              onClick={() => setFormValue('payment_type', paymentTypes.bambora)}
            >
              <RadioInput
                name="payment-method"
                tabIndex={shouldBeUnfocusable ? -1 : 0}
                checked={paymentType === paymentTypes.bambora}
                onChange={() =>
                  setFormValue('payment_type', paymentTypes.bambora)
                }
                label={i18n.gettext('Credit card')}
                labelStyle={{ fontWeight: 'initial' }}
              />
            </PaymentType>
          )}
          {hasPayPalProvider && (
            <PaymentType
              onClick={() => setFormValue('payment_type', paymentTypes.paypal)}
            >
              <RadioInput
                name="payment-method"
                tabIndex={shouldBeUnfocusable ? -1 : 0}
                checked={paymentType === paymentTypes.paypal}
                onChange={() =>
                  setFormValue('payment_type', paymentTypes.paypal)
                }
                label={i18n.gettext('PayPal')}
                labelStyle={{ fontWeight: 'initial' }}
              />
            </PaymentType>
          )}
        </RadioButtons>
        {hasStripeProvider || hasBamboraProvider ? <CreditCardIcons /> : null}
        <PaymentBlock>
          {hasStripeProvider &&
            stripeSepaEnabled &&
            formValues.get('payment_type') === paymentTypes.sepa &&
            ibanElement}
          {hasStripeProvider &&
            formValues.get('payment_type') === paymentTypes.credit &&
            creditElement}
          {hasPayPalProvider &&
            formValues.get('payment_type') === paymentTypes.paypal &&
            payPalElement}
        </PaymentBlock>
      </Container>
    );
  }
}

PaymentOptions.propTypes = {
  stripe: PropTypes.object,
  form: PropTypes.object,
  payPalDirectBuy: PropTypes.bool,
  price: PropTypes.number,
};

export default stripePublishKey ? injectStripe(PaymentOptions) : PaymentOptions;
